import { API_BASE_URL } from "../config";

const REFRESH_PATH = "/auth/refresh";

const getStoredToken = () => localStorage.getItem("rh_access_token");
const getStoredRefreshToken = () => localStorage.getItem("rh_refresh_token");

let refreshInFlight = null;
let onSessionExpired = null;

// Lets AuthContextWeb register a callback so its React state (not just
// localStorage) gets cleared when a session can no longer be refreshed.
export const setSessionExpiredHandler = (handler) => {
  onSessionExpired = handler;
};

const normalizePath = (path) => {
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }
  return path.startsWith("/")
    ? `${API_BASE_URL}${path}`
    : `${API_BASE_URL}/${path}`;
};

const toJsonIfPossible = async (response) => {
  const text = await response.text();
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

const refreshToken = async () => {
  if (refreshInFlight) return refreshInFlight;

  const existingRefreshToken = getStoredRefreshToken();
  if (!existingRefreshToken) {
    throw new Error("No refresh token available.");
  }

  refreshInFlight = fetch(normalizePath(REFRESH_PATH), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken: existingRefreshToken }),
  })
    .then(async (response) => {
      const payload = await toJsonIfPossible(response);
      if (!response.ok) {
        const message =
          payload?.message || payload?.error || "Session refresh failed.";
        throw new Error(message);
      }

      if (!payload?.token) {
        throw new Error("Refresh succeeded but no token was returned.");
      }

      localStorage.setItem("rh_access_token", payload.token);
      if (payload.refreshToken) {
        localStorage.setItem("rh_refresh_token", payload.refreshToken);
      }
      return payload.token;
    })
    .finally(() => {
      refreshInFlight = null;
    });

  return refreshInFlight;
};

export const clearSession = () => {
  localStorage.removeItem("rh_access_token");
  localStorage.removeItem("rh_refresh_token");
  localStorage.removeItem("rh_user");
};

export async function apiRequest(path, options = {}) {
  const {
    headers = {},
    auth = true,
    parseJson = true,
    retryOnAuthFailure = true,
    ...rest
  } = options;

  const mergedHeaders = { ...headers };

  if (auth) {
    const token = getStoredToken();
    if (token) {
      mergedHeaders.Authorization = `Bearer ${token}`;
    }
  }

  const response = await fetch(normalizePath(path), {
    ...rest,
    headers: mergedHeaders,
  });

  if (response.status === 401 && auth && retryOnAuthFailure) {
    try {
      const newToken = await refreshToken();
      return apiRequest(path, {
        ...options,
        retryOnAuthFailure: false,
        headers: {
          ...headers,
          Authorization: `Bearer ${newToken}`,
        },
      });
    } catch {
      clearSession();
      onSessionExpired?.();
      throw new Error("SESSION_EXPIRED");
    }
  }

  if (!parseJson) {
    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}`);
    }
    return response;
  }

  const payload = await toJsonIfPossible(response);

  if (!response.ok) {
    const message =
      payload?.message ||
      payload?.error ||
      `Request failed with status ${response.status}`;
    throw new Error(message);
  }

  return payload;
}
